/**
 * BUTTON
 */
import { faCircleNotch, IconDefinition } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { connect } from "react-redux"
import { Session } from "@/redux/_session.types"
import ReactTooltip from "react-tooltip"
import { useEffect, useRef, useState } from "react"

interface StateProps {
  _session: Session
}

export type ButtonClassName = "primary" | "secondary" | "light" | "med" | "warning" | ""

interface OwnProps {
  children?: any //Text inside
  color?: string //Color of the button
  width?: number
  icon?: IconDefinition //Fontawesome icon
  className?: ButtonClassName //Classname (primary, secondary, light)
  classNameCustom?: string //Custom className
  isFullWidth?: boolean //Take all width of the parent
  isLarge?: boolean //Is a big button
  isLoading?: boolean //Show loading spinner
  isWithBorder?: boolean //Show border
  onClick?: Function //Action to trigger on click
  type?: string //Type of button (exemple "submit")
  tooltip?: string
  disabled?: boolean
}

type Props = StateProps & OwnProps

function Button(props: Props) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [dims, setDims] = useState<{ width?: number; height?: number }>({})
  const [isLoading, setIsLoading] = useState(props.isLoading)

  //Show loading during 250ms for visual feedback
  useEffect(() => {
    if (props.isLoading) {
      setIsLoading(true)
    } else {
      setTimeout(() => {
        setIsLoading(false)
      }, 250)
    }
  }, [props.isLoading])

  useEffect(() => {
    if (contentRef.current) {
      const { offsetWidth, offsetHeight } = contentRef.current
      setDims({ width: offsetWidth, height: offsetHeight })
    }
  }, [])

  //Action on click
  function click(e) {
    if (props.disabled) return
    if (props.onClick) {
      props.onClick(e)
    }
  }

  //Set class of the button
  //différent if is large
  function getClassName() {
    let className = "button grey-t"

    if (props.isLarge) {
      className = className + " button-large"
    }

    if (props.classNameCustom) {
      className = className + " " + props.classNameCustom
    }

    return className
  }

  //Set style
  //className == "primary"
  //> call to action
  //className === "secondary"
  //> only the text is highlight
  //className === "light"
  //> text is light grey
  //isFullWidth
  //> take all width from parent
  function getStyle() {
    let border
    let backgroundColor
    let color
    let margin
    let maxWidth
    let opacity
    let cursor
    let width

    if (props.width) {
      width = props.width
    }

    if (props.color) {
      backgroundColor = props.color
      border = "1px solid " + backgroundColor
      color = "white"
    } else if (props.className === "primary") {
      backgroundColor = props._session.accountColors.cta
      border = "1px solid " + backgroundColor
      color = "white"
    } else if (props.className === "secondary") {
      color = props._session.accountColors.cta
    } else if (props.className === "light") {
      color = "#CCCCCC"
    } else if (props.className === "warning") {
      backgroundColor = "#eb5a46"
      color = "white"
    }

    if (props.isFullWidth) {
      margin = "6px 0px"
      maxWidth = "100%"
    }

    if (!border && props.isWithBorder) {
      border = "1px solid"
    }

    if (props.disabled) {
      opacity = 0.5
      backgroundColor = backgroundColor || "#F2F2F2"
      color = color || "#999999"
      cursor = "not-allowed"
    }

    return {
      border,
      backgroundColor,
      color,
      margin,
      maxWidth,
      opacity,
      cursor: cursor || "pointer",
      width
    }
  }

  return (
    <button
      className={getClassName()}
      data-tip={props.tooltip}
      onClick={click}
      style={getStyle()}
      disabled={props.disabled}>
      {isLoading ? (
        <div
          style={{
            width: dims.width,
            height: dims.height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
          <FontAwesomeIcon
            icon={faCircleNotch}
            spin
            style={{ color: props.className === "primary" ? "white" : props._session.accountColors.cta }}
          />
        </div>
      ) : (
        <div ref={contentRef}>
          {props.icon && <FontAwesomeIcon icon={props.icon} />}
          {props.children}
        </div>
      )}
      <ReactTooltip className="tooltip" />
    </button>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session
})

export default connect(mapStateToProps)(Button)
